import { Task } from 'components/Icons';
import InlineMessage from 'components/InlineMessage';
import ConfirmationDialogue from 'components/modals/ConfirmationDialogue';
import styles from 'components/projects/ProjectDeleteConfirmationDialogue.module.scss';
import TypeToConfirm from 'components/TypeToConfirm';
import React, { useState } from 'react';
import FontAwesome from 'react-fontawesome';
import { useSelector } from 'react-redux';
import { makeGetTagXhrStatusStateByKey } from 'redux/modules/tags.selectors';
import type { TagDraft } from 'redux/modules/tags.types';
import type { IState } from 'redux/state';
import type { Tag } from 'types/tag';

interface Props {
  tag: Tag | TagDraft | null;
  setTagActionDetails: (tag: Tag | TagDraft | null) => void;
  handleDeleteTag: () => void;
  handleArchiveTag: (tagId: number) => void;
}

const TagDeleteConfirmationDialogue: React.FC<Props> = ({
  tag,
  setTagActionDetails,
  handleDeleteTag,
}) => {
  const getTagXhrStatus = makeGetTagXhrStatusStateByKey();
  const key = tag?.id?.toString() || '';
  const isDeleting = useSelector(state =>
    getTagXhrStatus(state as IState, { key: `${key}-deleting` })
  ).isSaving;

  const [hasTypedToConfirm, setHasTypedToConfirm] = useState(false);

  if (!tag) {
    return null;
  }

  return (
    <ConfirmationDialogue
      title={`Delete ${tag.name}`}
      buttons={[
        {
          text: "Cancel, don't delete",
          priority: 'high',
          disabled: isDeleting,
          onClick: () => setTagActionDetails(null),
        },
      ]}
      isOpen={tag != null}
      onClose={() => setTagActionDetails(null)}
      width={600}
    >
      <div className={styles.warningContent}>
        <InlineMessage type={hasTypedToConfirm ? 'alert' : 'neutral'}>
          <ul className={styles.consequencesList}>
            <li>
              <span className={styles.projectBadgeWrap}>
                {tag.emoji && (
                  <img src={tag.emoji} className={styles.emoji} alt={`Emoji for ${tag.name}`} />
                )}
              </span>
              <p style={{ lineHeight: 1.3 }}>
                <strong>{tag.name}</strong> will be permanently deleted
              </p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <FontAwesome style={{ marginRight: 5, color: '#697077' }} name="clock" />
              </span>
              <p>This tag will be removed from all logged time entries</p>
            </li>
            <li>
              <span className={styles.iconWrap}>
                <Task fill="#697077" />
              </span>
              <p>This tag will be removed from all tasks</p>
            </li>
          </ul>
          <div className={styles.confirmWrap}>
            <p>Type DELETE to confirm</p>
            <TypeToConfirm
              confirmString="DELETE"
              onConfirm={handleDeleteTag}
              onChange={setHasTypedToConfirm}
              confirmButtonProps={{
                text: 'Permanently delete tag',
                icon: <FontAwesome style={{ marginRight: 5 }} name="trash" />,
                priority: 'high',
                destructive: true,
                isLoading: isDeleting,
                disabled: !hasTypedToConfirm || isDeleting,
              }}
            />
          </div>
        </InlineMessage>
      </div>
    </ConfirmationDialogue>
  );
};

export default TagDeleteConfirmationDialogue;
